import { Alert, Box, Button, FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { ConditionalBox } from "../../../../utils/ConditionalBox";
import { GrayTextField } from "../../../../utils/GrayTextField";
import { plural } from "../../../../utils/utilityFunctions";
import { connectionTypeDisagrees, fuseRatingDisagrees, phaseListDisagrees, serviceBranchConnectionDisagrees } from "./formConditionFunctions";

export default function IcpInspectionStepComments({
    formState: {
        comments, loopImpedanceOK, phaseList, phaseRotation,
        loopImpedanceTestLocation, loopImpedanceProblemPhases, loopImpedanceReadings, loopImpedanceAtNetwork,
        serviceBranchConnection,
        connectionType,
        fuseRating, fuseRatingOther,
        rippleControllerPhase,
    },
    networkApprovedPhaseList,
    networkApprovedParameters,
    dispatch,
    onBack,
    onNext}) {

    const { handleSubmit, control, formState: { errors } } = useForm();

    const onSubmit = (data) => {
        // console.log('IcpInspectionStepComments onSubmit passed validation with data', data)
        onNext(data)
    }

    const networkApprovedPhaseString = networkApprovedPhaseList?.join(', ')

    // const rippleControllerRecommendation = rippleControllerRecommends(networkApprovedParameters?.rippleControllerRequired)

    const commentAlertList = []
    if (serviceBranchConnectionDisagrees(serviceBranchConnection, networkApprovedParameters?.connectionPoint)) {
        commentAlertList.push({fieldName: 'Service Branch Connection', valueChosen: serviceBranchConnection, recommendation: networkApprovedParameters?.connectionPoint})
    }
    if (connectionTypeDisagrees(connectionType, networkApprovedParameters?.serviceMain)) {
        commentAlertList.push({fieldName: 'Connection Type', valueChosen: connectionType, recommendation: networkApprovedParameters?.serviceMain})
    }
    if (fuseRatingDisagrees(fuseRating, fuseRatingOther, networkApprovedParameters?.fuseRating)) {
        commentAlertList.push({fieldName: 'Fuse Rating', valueChosen: fuseRating, recommendation: networkApprovedParameters?.fuseRating})
    }
    if (phaseListDisagrees(phaseList, networkApprovedPhaseList)) {
        commentAlertList.push({fieldName: 'Phase', valueChosen: phaseList, recommendation: networkApprovedPhaseString})
    }
    // if (rippleControllerPhaseDisagrees(rippleControllerPhase, networkApprovedParameters?.rippleControllerRequired)) {
    //     commentAlertList.push({fieldName: 'Ripple Controller Phase', valueChosen: rippleControllerPhase, recommendation: rippleControllerRecommendation})
    // }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="h4" sx={{mb:3}}>Testing and Comments</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column' }}>

                <ConditionalBox condition={phaseList?.length === 3}>
                    <FormControl sx={{my:2}} error={!!errors.phaseRotation}>
                        <FormLabel color="secondary">Phase Rotation *</FormLabel>
                        <Controller name='phaseRotation' control={control}
                            defaultValue={phaseRotation}
                            rules={{required: true}}
                            render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                <RadioGroup value={phaseRotation}
                                        onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'phaseRotation', value: e.target.value})}}>
                                    <FormControlLabel key="Normal"  value="Normal"  control={<Radio />} label="Normal" />
                                    <FormControlLabel key="Reversed" value="Reversed" control={<Radio />} label="Reversed" />
                                </RadioGroup>
                            )}
                        />
                        {errors.phaseRotation && <FormHelperText>Phase Rotation is required</FormHelperText>}
                    </FormControl>
                </ConditionalBox>

                <FormControl sx={{my:2}} error={!!errors.loopImpedanceOK}>
                    <FormLabel color="secondary">Is Loop Impedance OK? *</FormLabel>
                    <Controller name='loopImpedanceOK' control={control}
                        defaultValue={loopImpedanceOK}
                        rules={{required: true}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <RadioGroup name="" value={loopImpedanceOK}
                                    onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'loopImpedanceOK', value: e.target.value})}}>
                                <FormControlLabel key="No"  value="No"  control={<Radio />} label="No" />
                                <FormControlLabel key="Yes" value="Yes" control={<Radio />} label="Yes" />
                            </RadioGroup>
                        )}
                    />
                    {errors.loopImpedanceOK && <FormHelperText>Loop Impedance is required</FormHelperText>}
                </FormControl>

                <ConditionalBox condition={loopImpedanceOK === "No"}>
                    <FormControl sx={{my:2}} error={!!errors.loopImpedanceTestLocation}>
                        <FormLabel color="secondary">Loop Impedance Test Location *</FormLabel>
                        <Controller name='loopImpedanceTestLocation' control={control}
                            defaultValue={loopImpedanceTestLocation}
                            rules={{required: true}}
                            render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                <RadioGroup name="" value={loopImpedanceTestLocation}
                                        onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'loopImpedanceTestLocation', value: e.target.value})}}>
                                    <FormControlLabel key="Network Connection Point"  value="Network Connection Point"  control={<Radio />} label="Network Connection Point (e.g Pillar)" />
                                    <FormControlLabel key="Meter Station" value="Meter Station" control={<Radio />} label="Meter Station" />
                                    <FormControlLabel key="Inside Installation" value="Inside Installation" control={<Radio />} label="Inside Installation (e.g. Household Power Socket)" />
                                </RadioGroup>
                            )}
                        />
                    </FormControl>
                    <ConditionalBox condition={phaseList?.length > 1}>
                        <FormControl sx={{my:2}} error={!!errors.loopImpedanceProblemPhases}>
                            <FormLabel color="secondary">Loop Impedance Problem Phases *</FormLabel>
                            <Controller name='loopImpedanceProblemPhases' control={control}
                                defaultValue={loopImpedanceProblemPhases}
                                rules={{required: true}}
                                render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                    <GrayTextField
                                        sx={{my:1}}
                                        label={`Phase(s) with high loop impedance`}
                                        value={loopImpedanceProblemPhases}
                                        error={!!errors.loopImpedanceProblemPhases}
                                        helperText={errors.loopImpedanceProblemPhases && "Loop Impedance Problem Phases are required"}
                                        onChange={(e) => { field.onChange(e.target.value); dispatch({type: 'loopImpedanceProblemPhases', value: e.target.value}) } }
                                    />
                                )}
                            />
                        </FormControl>
                    </ConditionalBox>
                    <FormControl sx={{my:2}} error={!!errors.loopImpedanceReadings}>
                        <FormLabel color="secondary">Loop Impedance Readings at {loopImpedanceTestLocation} * (Ohms)</FormLabel>
                        <Controller name='loopImpedanceReadings' control={control}
                            defaultValue={loopImpedanceReadings}
                            rules={{required: true}}
                            render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                <GrayTextField
                                    sx={{my:1}}
                                    label={`E.g. Red 1.88, White 0.39, Blue 0.44`}
                                    value={loopImpedanceReadings}
                                    error={!!errors.loopImpedanceReadings}
                                    helperText={errors.loopImpedanceReadings && "Loop Impedance Readings are required"}
                                    onChange={(e) => { field.onChange(e.target.value); dispatch({type: 'loopImpedanceReadings', value: e.target.value}) } }
                                />
                            )}
                        />
                    </FormControl>
                    <ConditionalBox condition={loopImpedanceTestLocation === 'Meter Station' || loopImpedanceTestLocation === 'Inside Installation' }>
                        <FormLabel color="secondary">Loop Impedance Readings at Network Connection Point * (Ohms)</FormLabel>
                        <Controller name='loopImpedanceAtNetwork' control={control}
                                defaultValue={loopImpedanceAtNetwork}
                                rules={{required: true}}
                                render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                    <GrayTextField
                                        sx={{my:1}}
                                        label={`E.g. Red 1.88, White 0.39, Blue 0.44`}
                                        value={loopImpedanceAtNetwork}
                                        error={!!errors.loopImpedanceAtNetwork}
                                        helperText={errors.loopImpedanceAtNetwork && "Loop Impedance Readings are required"}
                                        onChange={(e) => { field.onChange(e.target.value); dispatch({type: "loopImpedanceAtNetwork", value: e.target.value}) } }
                                    />
                                )}
                            />
                    </ConditionalBox>
                </ConditionalBox>

                <FormControl sx={{my:2}}  error={!!errors.comments}>
                    <CommentAlertBox commentList={commentAlertList} />
                    <Controller name='comments' control={control}
                        defaultValue={comments}
                        rules={{required: commentAlertList.length > 0}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <GrayTextField
                                sx={{my:1}}
                                label="Comments for Northpower"
                                value={comments}
                                error={!!errors.comments}
                                helperText={errors.comments && "Comments are required"}
                                multiline minRows={4} maxRows={6}
                                onChange={(e) => { field.onChange(e.target.value); dispatch({type: 'comments', value: e.target.value}) } }
                            />
                        )}
                    />
                </FormControl>

            </Box>
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={onBack}>Back</Button>
                <Button type="submit" variant="contained">Next</Button>
            </Box>
            {
                Object.keys(errors).length > 0
                &&
                <Box textAlign="right">
                    <Alert sx={{display: 'inline-flex'}} severity="error">We found {Object.keys(errors).length} error{plural(Object.keys(errors).length)}.</Alert>
                </Box>
            }
        </form>
    )
}

function CommentAlertBox({commentList}) {
    console.log('CommentAlertBox commentList', commentList)
    return (
        <ConditionalBox condition={commentList?.length > 0}>
            <Alert severity="info">
                <Typography variant="body2" key="intro" paragraph>Some of your selections don't match the expected options from the Network Approval form.</Typography>
                {
                    commentList.map(({fieldName, valueChosen, recommendation}) => {
                        const valueString = (Array.isArray(valueChosen) ? valueChosen.join(', ') : valueChosen)
                        return (
                            <Typography variant="body2" key={fieldName}><strong>{fieldName}</strong>: You chose <strong>{valueString}</strong>. The Network Approval form has <strong>{recommendation}</strong>.</Typography>
                        )
                    })
                }
                {/* <Typography variant="body2" key="intro" sx={{mt:2}}>Please add comments explaining your choices.</Typography> */}
                <Typography variant="body2" key="intro" sx={{mt:2}}>Please double-check your entry. If Northpower's information about this job is wrong, please comment below with details.</Typography>
            </Alert> 

        </ConditionalBox>
    )
}